import { TypographyP2, TypographyP2SemiBold } from '@heidi/design-system/typography';
import type { JSX, ReactNode } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import {
  AnchorList,
  AnchorListLoading,
  PaginatedAnchorList,
} from './ObjectWorkspaceAnchorList';
import { ObjectWorkspacePatientDetails } from './ObjectWorkspacePatientDetails';
import { ObjectWorkspaceWorkContent } from './ObjectWorkspaceWorkContent';
import type {
  ObjectWorkspaceAnchor,
  ObjectWorkspaceDocumentsState,
  ObjectWorkspaceFacet,
  ObjectWorkspaceObject,
  ObjectWorkspacePatientDetailsState,
  ObjectWorkspacePatientSessionsState,
  ObjectWorkspaceTask,
  ObjectWorkspaceTasksState,
} from './objectWorkspace.data';

interface AnchorSectionProps {
  heading: MessageDescriptor;
  children: ReactNode;
}

function AnchorSection({ heading, children }: AnchorSectionProps): JSX.Element {
  const intl = useIntl();

  return (
    <section className="flex flex-col gap-3">
      <TypographyP2SemiBold className="text-text-primary">
        {intl.formatMessage(heading)}
      </TypographyP2SemiBold>
      {children}
    </section>
  );
}

function FacetMessage({ message }: { message: MessageDescriptor }): JSX.Element {
  const intl = useIntl();

  return (
    <TypographyP2 className="text-text-secondary">
      {intl.formatMessage(message)}
    </TypographyP2>
  );
}

function PatientSessionsFacet({
  state,
  onSelect,
}: {
  state: ObjectWorkspacePatientSessionsState;
  onSelect?: (anchor: ObjectWorkspaceAnchor) => void;
}): JSX.Element {
  let content: ReactNode;
  if (state.status === 'loading') {
    content = <AnchorListLoading />;
  } else if (state.status === 'error') {
    content = <FacetMessage message={messages.patientSessionsUnavailable} />;
  } else if (state.sessions.length === 0) {
    content = <FacetMessage message={messages.noPatientSessions} />;
  } else {
    content = (
      <PaginatedAnchorList
        anchors={state.sessions}
        onSelect={onSelect}
        hasMore={state.hasMore}
        isLoadingMore={state.isLoadingMore}
        onShowMore={state.onShowMore}
      />
    );
  }

  return <AnchorSection heading={messages.sessionsHeading}>{content}</AnchorSection>;
}

function DocumentsFacet({
  state,
  onSelect,
}: {
  state: ObjectWorkspaceDocumentsState;
  onSelect?: (anchor: ObjectWorkspaceAnchor) => void;
}): JSX.Element {
  let content: ReactNode;
  if (state.status === 'loading') {
    content = <AnchorListLoading label={messages.loadingDocuments} />;
  } else if (state.status === 'error') {
    content = <FacetMessage message={messages.documentsUnavailable} />;
  } else if (state.documents.length === 0) {
    content = <FacetMessage message={messages.noDocuments} />;
  } else {
    content = <AnchorList anchors={state.documents} onSelect={onSelect} />;
  }

  return <AnchorSection heading={messages.documentsHeading}>{content}</AnchorSection>;
}

export function ObjectWorkspaceFacetContent({
  facet,
  kind,
  patientDetailsState,
  patientSessionsState,
  tasksState,
  documentsState,
  selectedTaskIds,
  onAnchorSelect,
  onDocumentSelect,
  onTaskToggle,
}: {
  facet: ObjectWorkspaceFacet;
  kind: ObjectWorkspaceObject['kind'];
  patientDetailsState?: ObjectWorkspacePatientDetailsState;
  patientSessionsState?: ObjectWorkspacePatientSessionsState;
  tasksState?: ObjectWorkspaceTasksState;
  documentsState?: ObjectWorkspaceDocumentsState;
  selectedTaskIds: readonly string[];
  onAnchorSelect?: (anchor: ObjectWorkspaceAnchor) => void;
  onDocumentSelect?: (document: ObjectWorkspaceAnchor) => void;
  onTaskToggle?: (task: ObjectWorkspaceTask) => void;
}): JSX.Element {
  if (facet === 'work') {
    return (
      <ObjectWorkspaceWorkContent
        state={tasksState ?? { status: 'ready', tasks: [] }}
        selectedTaskIds={selectedTaskIds}
        onTaskToggle={onTaskToggle}
      />
    );
  }
  if (facet === 'details' && kind === 'patient') {
    return (
      <ObjectWorkspacePatientDetails
        state={patientDetailsState ?? { status: 'ready', details: {} }}
      />
    );
  }
  if (facet === 'sessions') {
    return (
      <PatientSessionsFacet
        state={patientSessionsState ?? { status: 'ready', sessions: [] }}
        onSelect={onAnchorSelect}
      />
    );
  }
  return (
    <DocumentsFacet
      state={documentsState ?? { status: 'ready', documents: [] }}
      onSelect={onDocumentSelect}
    />
  );
}
